import {
  AccountDomain,
  AccountDomainStatus,
} from '@/client/accounts/types/account-domain.type';
import { LoadingStatuses } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { LanguageContext } from '@/common/context/LanguageContext';
import { useRemoveDomain, useSendVerificationEmail } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui';
import { formatDate } from '@/utils/date/date.helper';
import { TrashIcon } from '@/utils/helpers';
import { getAppAbsoluteUrl } from '@/utils/url/url.helper';
import { InputText } from 'primereact/inputtext';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ForceVerifyRow } from './ForceVerifyRow';

const StyledDeleteButton = styled.button<{ showOnHover?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  overflow: hidden;
  max-width: ${({ showOnHover }) => (showOnHover ? '0' : '20px')};
  opacity: ${({ showOnHover }) => (showOnHover ? '0' : '1')};
  transition: max-width 0.25s ease, opacity 0.25s ease, margin 0.25s ease;

  svg {
    width: 14px;
    height: 16px;
    flex-shrink: 0;

    path {
      fill: var(--red-main);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledDomainCard = styled.div<{
  expanded?: boolean;
  clickable?: boolean;
}>`
  display: flex;
  flex-direction: ${({ expanded }) => (expanded ? 'column' : 'row')};
  align-items: ${({ expanded }) => (expanded ? 'flex-start' : 'center')};
  padding: var(--default-padding-small);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  background-color: var(--beige-main);
  gap: ${({ expanded }) => (expanded ? 'var(--medium-padding)' : '0')};
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};

  &:hover ${StyledDeleteButton}[data-show-on-hover] {
    max-width: 20px;
    opacity: 1;
    margin-left: 0;
  }
`;

const StyledDomainCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledDomainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  flex: 1;
  min-width: 0;
`;

const StyledDomainName = styled.span`
  font-weight: 700;
  font-size: var(--medium-font-size);
  color: var(--black-main);
  line-height: 1.4;
`;

const StyledVerifiedDate = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  color: var(--black-main);
  line-height: 1.4;
  white-space: nowrap;
`;

const StyledStatusBadge = styled.span<{
  variant: 'verified' | 'pending-verification' | 'non-verified';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--xxxsmall-padding) 9px;
  border-radius: var(--grand-border-radius);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;

  ${({ variant }) =>
    variant === 'verified'
      ? css`
          background-color: var(--green-pale);
          color: var(--green-main);
        `
      : variant === 'pending-verification'
      ? css`
          background-color: var(--yellow-pale);
          color: var(--yellow-dark);
        `
      : css`
          background-color: var(--red-pale);
          color: var(--red-main);
        `}
`;

const StyledStatusActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--xsmall-padding);
  flex-shrink: 0;

  > ${StyledDeleteButton}[data-show-on-hover] {
    margin-left: calc(-1 * var(--xsmall-padding));
  }
`;

const StyledChevron = styled.button<{ expanded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: var(--default-padding);
  height: var(--default-padding);
  transition: transform 0.2s;
  transform: ${({ expanded }) =>
    expanded ? 'rotate(180deg)' : 'rotate(0deg)'};

  i {
    font-size: var(--small-font-size);
    color: var(--black-main);
  }
`;

const StyledExpandedContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledVerificationInfo = styled.p`
  font-size: var(--small-font-size);
  font-weight: 400;
  color: var(--black-main);
  margin: 0;
  line-height: 1.4;
`;

const StyledVerificationForm = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--medium-padding);
  width: 100%;
  margin-block: var(--medium-padding);
`;

const StyledInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding);
  flex: 1;
`;

const StyledInputError = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--red-main);
  line-height: 1.5;
`;

const StyledInputSuccess = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--green-main);
  line-height: 1.5;
`;

type DomainCardProps = {
  domain: AccountDomain;
  accountId: string;
  canForceVerify?: boolean;
  adminUserCount?: number;
};

export const DomainCard: React.FC<DomainCardProps> = ({
  domain,
  accountId,
  canForceVerify = false,
  adminUserCount = 0,
}) => {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const { setDialogData } = useContext(DialogContext);
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const { sendVerification, isLoading: isSending } =
    useSendVerificationEmail(accountId);
  const { removeDomain, isLoading: isRemoving } = useRemoveDomain(accountId);

  const isVerified = domain.status === AccountDomainStatus.VERIFIED;
  const isPendingVerification =
    domain.status === AccountDomainStatus.PENDING_VERIFICATION;
  const isNonVerified = domain.status === AccountDomainStatus.NON_VERIFIED;
  const isExpandable = isPendingVerification || isNonVerified;
  const hasToken = !!domain.token;
  const showForceVerify = canForceVerify && isExpandable && expanded;

  const badgeVariant = isVerified
    ? 'verified'
    : isPendingVerification
    ? 'pending-verification'
    : 'non-verified';

  const badgeLabel = isVerified
    ? t('account.domains.status.verified')
    : isPendingVerification
    ? t('account.domains.status.pendingVerification')
    : t('account.domains.status.nonVerified');

  const validateEmail = (value: string): boolean => {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      setEmailError(t('account.domains.verification.emailRequired'));
      return false;
    }
    if (!normalizedValue.endsWith(`@${domain.domain.toLowerCase()}`)) {
      setEmailError(
        t('account.domains.verification.emailMustEndWith', {
          domain: domain.domain,
        }),
      );
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedValue)) {
      setEmailError(t('account.domains.verification.invalidEmail'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSendVerification = async () => {
    if (!validateEmail(email)) return;

    try {
      await sendVerification(domain.id, {
        email: email.trim().toLowerCase(),
        language,
        appUrl: getAppAbsoluteUrl('/'),
      });
      toast?.success(
        t('toast.success'),
        t('account.domains.verification.linkSent'),
      );
      setEmail('');
    } catch {
      toast?.error(
        t('toast.error'),
        t('account.domains.verification.sendFailed'),
      );
    }
  };

  const handleRemove = async () => {
    try {
      await removeDomain(domain.id);
      toast?.success(t('toast.success'), t('account.domains.removed'));
    } catch {
      toast?.error(t('toast.error'), t('account.domains.removeFailed'));
    }
  };

  const confirmRemoveVerified = () => {
    setDialogData({
      type: DialogType.WARNING,
      show: true,
      showDialogConfirmation: false,
      header: t('account.domains.remove.modal.title'),
      customContent: (
        <>
          <p>
            {t('account.domains.remove.modal.body1', {
              domain: domain.domain,
            })}
          </p>
          <p>
            {t('account.domains.remove.modal.body2', {
              count: adminUserCount,
            })}
          </p>
        </>
      ),
      confirmButtonLabel: t('account.domains.remove.modal.confirm'),
      onAccept: handleRemove,
    });
  };

  return (
    <StyledDomainCard
      expanded={isExpandable && expanded}
      clickable={isExpandable}
      onClick={() => isExpandable && setExpanded(!expanded)}
    >
      <StyledDomainCardHeader>
        <StyledDomainInfo>
          <StyledDomainName>{domain.domain}</StyledDomainName>
          {isVerified && domain.updated && (
            <>
              <StyledVerifiedDate>|</StyledVerifiedDate>
              <StyledVerifiedDate>
                {t('account.domains.verifiedOn', {
                  date: formatDate(domain.updated, DateFormats.DATEONLY),
                })}
              </StyledVerifiedDate>
            </>
          )}
        </StyledDomainInfo>
        <StyledStatusActions>
          <StyledStatusBadge variant={badgeVariant}>
            {badgeLabel}
          </StyledStatusBadge>
          {isVerified && (
            <StyledDeleteButton
              onClick={confirmRemoveVerified}
              disabled={isRemoving}
              type="button"
              showOnHover
              data-show-on-hover
            >
              {isRemoving ? (
                <i className="pi pi-spinner pi-spin" />
              ) : (
                <TrashIcon />
              )}
            </StyledDeleteButton>
          )}
          {isExpandable && expanded && (
            <StyledDeleteButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              disabled={isRemoving}
              type="button"
            >
              {isRemoving ? (
                <i className="pi pi-spinner pi-spin" />
              ) : (
                <TrashIcon />
              )}
            </StyledDeleteButton>
          )}
          {isExpandable && (
            <StyledChevron
              expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              type="button"
            >
              <i className="pi pi-chevron-down" />
            </StyledChevron>
          )}
        </StyledStatusActions>
      </StyledDomainCardHeader>

      {isExpandable && expanded && (
        <StyledExpandedContent onClick={(e) => e.stopPropagation()}>
          <StyledVerificationInfo>
            {hasToken
              ? t('account.domains.verification.resendInfo')
              : t('account.domains.verification.enterEmail')}
            <br />
            {hasToken ? null : t('account.domains.verification.sendLink')}
          </StyledVerificationInfo>
          <StyledVerificationForm>
            <StyledInputWrapper>
              <InputText
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                placeholder={t(
                  'account.domains.verification.emailPlaceholder',
                  { domain: domain.domain },
                )}
                className={emailError ? 'p-invalid white' : 'white'}
              />
              {emailError && <StyledInputError>{emailError}</StyledInputError>}
            </StyledInputWrapper>
            <AppButton
              label={
                hasToken
                  ? t('account.domains.verification.resendButton')
                  : t('account.domains.verification.sendButton')
              }
              state={isSending ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
              onClick={handleSendVerification}
              isDisabled={isSending}
            />
          </StyledVerificationForm>
          {hasToken && (
            <StyledInputSuccess>
              {t('account.domains.verification.linkSent')}
            </StyledInputSuccess>
          )}
        </StyledExpandedContent>
      )}

      {showForceVerify && (
        <ForceVerifyRow domain={domain} accountId={accountId} />
      )}
    </StyledDomainCard>
  );
};
